import { type ChangeEvent, type FormEvent, type SubmitEvent as ReactSubmitEvent, memo, useRef, useState } from "react";
import "Components/ConfigLayout/System/ManagementSettings/ManagementSettings.less";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import type { SystemManagementState } from "Interfaces";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import {
	selectSystemManagementSettings, setSystemManagementAction
} from "Data/Redux/Slices/Settings/System/Management";
import {
	Button, ButtonTypes, Card, Checkbox, ConfirmModal, type ModalButtonProps, Input, type SelectOption, Select,
	type SelectComponent, SettingsLayout
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import SystemDataModal from "Components/ConfigLayout/System/ManagementSettings/SystemDataModal";
import { isEqual } from "lodash";
import FirmwareUpgradeModal from "Components/ConfigLayout/System/ManagementSettings/FirmwareUpgradeModal";
import ProgressBarModal from "Components/ConfigLayout/System/ManagementSettings/ProgressBarModal";
import { setDefaultIpSettingsAction } from "Data/Redux/Slices/Settings/Network/IpSettings";

const passwordExpirationOptions: SelectOption[] =
	[ 0, 1, 2, 4, 6 ].map(value => {
		if (value === 0) {
			return {
				label: "Never",
				value
			};
		}

		return {
			label: `${ value } Month(s)`,
			value
		};
	});

type ManagementSettingsForm = {
	systemId: HTMLInputElement;
	deviceName: HTMLInputElement;
	passwordExpiration: SelectComponent;
} & HTMLFormElement;

const confirmButton: ModalButtonProps = {
	text: "Confirm",
	type: ButtonTypes.TERTIARY
};

const cancelButton: ModalButtonProps = {
	text: "Cancel",
	type: ButtonTypes.DANGER
};

const deviceFieldRegex = "^[a-zA-Z0-9!@#$%^&*()_+-=]{1,20}$";

function ManagementSettings() {
	const dispatch = useAppDispatch();
	const [ submitted, setSubmitted ] = useState(false);
	const systemManagement = useAppSelector(selectSystemManagementSettings);
	const [ localSystemManagement, setLocalSystemManagement ] = useState<SystemManagementState>(systemManagement);
	const setSystemManagement = () => dispatch(setSystemManagementAction(localSystemManagement));
	const factoryDefaultProgressBarModalRef = useRef<HTMLButtonElement>(null);
	const systemRebootProgressBarModalRef = useRef<HTMLButtonElement>(null);
	const [ hardReset, setHardReset ] = useState(false);

	const handleFactoryDefault = () => {
		if (hardReset) {
			dispatch(setDefaultIpSettingsAction());
		}

		factoryDefaultProgressBarModalRef.current?.click();
	};

	const handleVisibilityChange = () => {
		setHardReset(false);
	};

	const openSystemRebootProgressBarModal = () => systemRebootProgressBarModalRef.current?.click();

	const handleSubmit = (event: ReactSubmitEvent<ManagementSettingsForm>) => {
		event.preventDefault();
		setSubmitted(true);

		if (event.currentTarget?.checkValidity()) {
			setSystemManagement();
		}
	};

	const handleReset = (event: FormEvent<ManagementSettingsForm>) => {
		event.preventDefault();

		setLocalSystemManagement(systemManagement);
	};

	const handleSelect = (value: string | null) => {
		if (!value) {
			return;
		}

		const newState = produce(localSystemManagement, draft => {
			draft.paswordExpiryTerm = +value;
		});

		setLocalSystemManagement(newState);
	};

	const handleSystemIdUpdate = (value: string) => {
		const newState = produce(localSystemManagement, (draft) => {
			draft.systemId = value;
		});

		setLocalSystemManagement(newState);
	};

	const handleDeviceNameUpdate = (value: string) => {
		const newState = produce(localSystemManagement, (draft) => {
			draft.deviceName = value;
		});

		setLocalSystemManagement(newState);
	};

	const handleHardResetChange = (event: ChangeEvent<HTMLInputElement>) => {
		setHardReset(event.currentTarget.checked);
	};

	const factoryDefaultModalContent = (
		<div className="factory-default-modal-content">
			<span>This will load the factory default settings.</span>
			<Checkbox checked={ hardReset } onChange={ handleHardResetChange }>Include Network Settings</Checkbox>
		</div>
	);

	return (
		<SettingsLayout.Form
			className="management-settings"
			noValidate
			onReset={ handleReset }
			onSubmit={ handleSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="small" title="System Management">
					<div className="form-row">
						<span>System ID</span>
						<Input
							value={ localSystemManagement.systemId }
							name="systemId"
							noValidate={ !submitted }
							onUpdate={ handleSystemIdUpdate }
							required
							validator={ deviceFieldRegex }
							validityMessage="Invalid System ID"
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Device Name</span>
						<Input
							value={ localSystemManagement.deviceName }
							name="deviceName"
							noValidate={ !submitted }
							onUpdate={ handleDeviceNameUpdate }
							required
							validator={ deviceFieldRegex }
							validityMessage="Invalid Device Name"
							wrapClassName="form-input"
						/>
					</div>
					<div className="form-row">
						<span>Expired Term Of Password</span>
						<Select
							allowClear={ false }
							className="form-select"
							name="passwordExpiration"
							onSelect={ handleSelect }
							options={ passwordExpirationOptions }
							value={ localSystemManagement.paswordExpiryTerm }
						/>
					</div>
					<div className="divider" />
					<div className="form-row">
						<span>Firmware Upgrade</span>
						<FirmwareUpgradeModal />
					</div>
					<div className="form-row">
						<span>Factory Default</span>
						<ConfirmModal
							okButton={ { ...confirmButton, onClick: handleFactoryDefault } }
							cancelButton={ cancelButton }
							title="Factory Default"
							modalContent={ factoryDefaultModalContent }
							onVisibilityChange={ handleVisibilityChange }
						>
							<Button type={ ButtonTypes.TERTIARY } htmlType="button">
								Load
							</Button>
						</ConfirmModal>
						<ProgressBarModal
							inProgressMessage="Factory Reset..."
							completeMessage="Factory reset completed successfully."
							title="Factory Default"
							totalDuration={ 10 }
						>
							<button ref={ factoryDefaultProgressBarModalRef } hidden type="button" />
						</ProgressBarModal>
					</div>
					<div className="form-row">
						<span>System Data</span>
						<Button type={ ButtonTypes.TERTIARY } htmlType="button">
							Save
						</Button>
						<SystemDataModal />
					</div>
					<div className="form-row">
						<span>System Reboot</span>
						<ConfirmModal
							okButton={ { ...confirmButton, onClick: openSystemRebootProgressBarModal } }
							cancelButton={ cancelButton }
							title="Reboot"
							modalContent={
								`The system will reboot. In some cases,
								if DHCP is enabled it may not pull a new IP address.`
							}
						>
							<Button type={ ButtonTypes.TERTIARY } htmlType="button">
								Reboot
							</Button>
						</ConfirmModal>
						<ProgressBarModal
							inProgressMessage="System Reboot..."
							completeMessage="System reboot completed successfully."
							title="System Reboot"
							totalDuration={ 10 }
						>
							<button ref={ systemRebootProgressBarModalRef } hidden type="button" />
						</ProgressBarModal>
					</div>
					<div className="form-row">
						<span>Event Log</span>
						<Button type={ ButtonTypes.TERTIARY } htmlType="button">
							Save
						</Button>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ isEqual(localSystemManagement, systemManagement) } />
		</SettingsLayout.Form>
	);
}

export default memo(ManagementSettings);
